import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Folder, FolderOpen, FileText, GitBranch, Loader2, Download, Search, FileCode, FileJson,
} from 'lucide-react';
import { apiService } from '../services/api';
import { toast } from 'sonner';
import IndexingBadge from './IndexingBadge';
import { useWorkspaceStatus } from '../hooks/useWorkspaceStatus';

interface RepoPanelProps {
  workspaceId: string;
}

interface FlatNode {
  type: 'dir' | 'file';
  path: string;
  name: string;
  depth: number;
  size?: number;
}

const fileIconFor = (name: string) => {
  if (name.endsWith('.json')) return <FileJson className="w-3.5 h-3.5 text-[#A1A1AA]" />;
  if (name.match(/\.(ts|tsx|js|jsx|py|go|rs|java)$/)) return <FileCode className="w-3.5 h-3.5 text-[#A1A1AA]" />;
  return <FileText className="w-3.5 h-3.5 text-[#52525B]" />;
};

// Iterative tree walker — emits ordered nodes filtered by open set.
const flattenTree = (
  rootNode: any,
  openDirs: Set<string>,
): FlatNode[] => {
  const out: FlatNode[] = [];
  const walk = (n: any, path: string, depth: number) => {
    const dirs = (n && n._dirs) || {};
    Object.keys(dirs).sort().forEach((dname) => {
      const fullDir = path ? `${path}/${dname}` : dname;
      out.push({ type: 'dir', path: fullDir, name: dname, depth });
      if (openDirs.has(fullDir)) {
        walk(dirs[dname], fullDir, depth + 1);
      }
    });
    const files = (n && n._files) || [];
    files.forEach((f: any) => {
      const fullFile = path ? `${path}/${f.name}` : f.name;
      out.push({ type: 'file', path: fullFile, name: f.name, depth, size: f.size });
    });
  };
  walk(rootNode, '', 0);
  return out;
};

// Normalize FastAPI error payloads (detail can be a string OR a dict).
const errorText = (e: any, fallback: string): string => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object') {
    return d.error || d.reason || JSON.stringify(d);
  }
  return e?.message || fallback;
};

const RepoPanel = (props: RepoPanelProps) => {
  const { workspaceId } = props;
  const [status, setStatus] = useState<any>(null);
  const [tree, setTree] = useState<any>(null);
  const [openDirs, setOpenDirs] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [gitUrl, setGitUrl] = useState('');
  const [branch, setBranch] = useState('main');
  const [githubToken, setGithubToken] = useState('');
  const [showTokenField, setShowTokenField] = useState(false);
  const [storedToken, setStoredToken] = useState<any>(null); // {has_token, masked_preview, status, ...}
  const [replacingToken, setReplacingToken] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string>('');
  const [fileContent, setFileContent] = useState<string>('');
  const [filter, setFilter] = useState('');
  const [indexStatus, setIndexStatus] = useState<any>(null);
  const [reindexing, setReindexing] = useState(false);

  // B20/B41 — virtualization state (declared at top to satisfy hook ordering)
  const ROW_HEIGHT = 24;
  const VIRTUALIZE_THRESHOLD = 250;
  const treeScrollRef = React.useRef<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportH, setViewportH] = useState(420);

  const refresh = useCallback(async () => {
    try {
      const s = await apiService.importStatus(workspaceId);
      setStatus(s);
      // Only fetch the tree if the repo is actually on disk. When the path
      // was wiped (e.g. pod restart on prod) and rehydrate needs a token,
      // /status returns has_import=true + needs_rehydrate=true and we must
      // surface that to the user instead of silently saying "no repo".
      if (s.has_import && !s.needs_rehydrate) {
        try {
          const t = await apiService.importTree(workspaceId);
          setTree(t.tree);
        } catch (treeErr: any) {
          // Tree API failed. DO NOT wipe the last-known tree — that
          // triggered the "workspace flashes empty then reloads" bug
          // in production (07 Jul 2026 iter i). We keep the previous
          // tree visible; the next successful refresh will replace it.
          // Only elevate to a real UI state change when the status
          // itself confirms the repo is gone.
          // If 409 (needs_token), reflect that in status
          if (treeErr?.response?.status === 409) {
            setStatus({ ...s, needs_rehydrate: true, rehydrate_reason: 'needs_token' });
          }
        }
        // Best-effort fetch of A4 index status (doesn't fail the panel if missing)
        try {
          const ix = await apiService.workspaceIndexStatus(workspaceId);
          setIndexStatus(ix);
        } catch {
          // Same stability principle — preserve last-known indexStatus.
        }
      } else if (s.has_import === false) {
        // Status is authoritative: the repo really isn't there. Drop
        // the stale tree so the empty state renders correctly.
        setTree(null);
      }
    } catch {
      // Network blip on /status: keep whatever we last showed instead
      // of blanking the panel. This eliminates the transient "no code"
      // flash reported repeatedly on production.
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  const onRehydrate = async () => {
    setImporting(true);
    try {
      const token = showTokenField ? githubToken.trim() : '';
      await apiService.rehydrateImport(workspaceId, token || undefined);
      toast.success('Repositorio reimportado');
      setGithubToken('');
      await refresh();
    } catch (e: any) {
      toast.error(errorText(e, 'Error al rehidratar'));
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => {
    refresh();
    // Load the stored GitHub token state once on mount — used to render
    // "Token guardado · ghp…XXXX" instead of an empty input on private repos.
    apiService.getGithubIntegration()
      .then(setStoredToken)
      .catch(() => setStoredToken(null));
  }, [refresh]);

  // ── Shared workspace status (Feb 2026, Emergent Support fix) ──────
  // Subscribe to the same hook DeployPanel uses so both panels react
  // to the SAME snapshot. Whenever the shared poll sees a new import
  // status, we call `refresh()` so the tree/index sync too. This
  // eliminates the two parallel polling loops that caused UI flicker
  // and "botones desaparecen" bugs on production.
  const sharedStatus = useWorkspaceStatus(workspaceId, 4000);
  const lastSyncRef = React.useRef<number>(0);
  useEffect(() => {
    if (!sharedStatus.lastUpdated || sharedStatus.lastUpdated === lastSyncRef.current) return;
    lastSyncRef.current = sharedStatus.lastUpdated;
    // Fold the shared snapshot into local state so the panel stays in
    // sync WITHOUT running its own /status poll on top.
    if (sharedStatus.importStatus) setStatus(sharedStatus.importStatus);
    if (sharedStatus.indexStatus) setIndexStatus(sharedStatus.indexStatus);
    // If the shared snapshot says the repo is present but our tree
    // hasn't loaded yet, refresh to pull the tree in.
    if (
      sharedStatus.importStatus?.has_import &&
      !sharedStatus.importStatus?.needs_rehydrate &&
      !tree
    ) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedStatus.lastUpdated]);

  // FASE 1 — Self-healing entry-point. Call /ensure EXACTLY ONCE after the
  // repo state is known. The endpoint is idempotent: it schedules an index
  // only if one is needed (no chunks + no running task), and is a no-op
  // otherwise. Polling never re-triggers this — that was the root cause of
  // the "progress resets to 0%" loop the user reported.
  const ensureCalledRef = React.useRef(false);
  useEffect(() => {
    if (ensureCalledRef.current) return;
    if (!status?.has_import || status?.needs_rehydrate) return;
    ensureCalledRef.current = true;
    apiService
      .workspaceIndexEnsure(workspaceId)
      .then(async () => {
        // Re-read status so the badge picks up any newly-scheduled job.
        try {
          const ix = await apiService.workspaceIndexStatus(workspaceId);
          setIndexStatus(ix);
        } catch {
          /* swallow — polling will refresh */
        }
      })
      .catch(() => {
        /* user can still hit Reindex manually */
      });
  }, [status?.has_import, status?.needs_rehydrate, workspaceId]);

  // Auto-poll the index status while it is actively building. Prevents the
  // UI from being stuck on "Sin índice" while the background task is in
  // flight; also recovers automatically if the task was lost.
  useEffect(() => {
    if (!indexStatus) return;
    const isBusy =
      indexStatus.indexing_state === 'running' ||
      indexStatus.indexing_state === 'scheduled';
    if (!isBusy) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const ix = await apiService.workspaceIndexStatus(workspaceId);
        if (!cancelled) setIndexStatus(ix);
      } catch {
        /* keep polling */
      }
    };
    const handle = setInterval(tick, 3000);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
    // Only re-bind when the indexing state changes; reading indexStatus
    // inside the effect is intentional. The lint rule's suggestion to add
    // `indexStatus` would cause infinite re-binding because we update it
    // from inside the tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indexStatus?.indexing_state, workspaceId]);

  // B20/B41 — bind virtualization scroll/resize listeners (top-level hook).
  useEffect(() => {
    const el = treeScrollRef.current;
    if (!el) return;
    const onScroll = () => setScrollTop(el.scrollTop);
    const onResize = () => setViewportH(el.clientHeight);
    onResize();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [tree]);

  // When the user toggles "private repo" ON, if there's already a saved token,
  // we are NOT in replacement mode — the import will use the stored token
  // server-side. Showing an input would be misleading.
  const tokenSaved = storedToken?.has_token && storedToken.status !== 'invalid';
  const tokenInvalid = storedToken?.has_token && storedToken.status === 'invalid';
  const tokenInputVisible = showTokenField && (!tokenSaved || replacingToken);

  const saveToken = async () => {
    const t = githubToken.trim();
    if (!t) {
      toast.error('Pega un token primero');
      return;
    }
    try {
      const out = await apiService.saveGithubToken(t);
      setStoredToken(out);
      setGithubToken('');
      setReplacingToken(false);
      toast.success(`Token guardado · ${out.github_username || 'GitHub'}`);
    } catch (e: any) {
      toast.error(errorText(e, 'No se pudo guardar el token'));
    }
  };

  const deleteToken = async () => {
    try {
      await apiService.deleteGithubToken();
      setStoredToken({ has_token: false });
      setReplacingToken(false);
      toast.success('Token eliminado');
    } catch (e: any) {
      toast.error(errorText(e, 'No se pudo eliminar'));
    }
  };

  const onImport = async () => {
    if (!gitUrl) return;
    setImporting(true);
    try {
      // If the user typed a new token, save it first so subsequent imports
      // (and rehydrates on other devices/sessions) reuse it transparently.
      const inputToken = tokenInputVisible ? githubToken.trim() : '';
      let effectiveToken: string | undefined;
      if (inputToken) {
        try {
          const out = await apiService.saveGithubToken(inputToken);
          setStoredToken(out);
          setReplacingToken(false);
          effectiveToken = inputToken;
        } catch (saveErr: any) {
          toast.error(errorText(saveErr, 'Token rechazado por GitHub'));
          setImporting(false);
          return;
        }
      }
      // If we didn't pass a token, the backend will fall back to the stored
      // one (when present) → seamless private-repo imports across sessions.
      const r = await apiService.importGit(workspaceId, gitUrl, branch, effectiveToken);
      toast.success(`Importados ${r.files_imported} archivos`);
      setGithubToken('');
      await refresh();
    } catch (e: any) {
      toast.error(errorText(e, 'Error al importar'));
    } finally {
      setImporting(false);
    }
  };

  const onSelectFile = async (p: string) => {
    setSelectedFile(p);
    setFileContent('Cargando…');
    try {
      const data = await apiService.readFile(workspaceId, p);
      if (data.binary) {
        setFileContent(`(archivo binario — ${data.size} bytes)`);
      } else {
        setFileContent(data.content || '');
      }
    } catch (e: any) {
      setFileContent(`Error al leer: ${errorText(e, 'error desconocido')}`);
    }
  };

  const toggleDir = (path: string) => {
    setOpenDirs((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="repo-loading">
        <Loader2 className="w-6 h-6 text-[#A1A1AA] animate-spin" />
      </div>
    );
  }

  // Has imported repo in DB but the filesystem path was wiped (pod restart).
  // Either rehydrate transparently failed and a token is required, or the
  // source itself can no longer be re-fetched.
  if (status?.has_import && status?.needs_rehydrate) {
    const src = status.import_source || {};
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-[#0E0F11] border border-yellow-500/20 rounded-xl p-8"
        data-testid="repo-needs-rehydrate"
      >
        <GitBranch className="w-12 h-12 text-yellow-500/70 mx-auto mb-4" strokeWidth={1.5} />
        <h3 className="text-xl text-white text-center mb-2">Repositorio temporalmente no disponible</h3>
        <p className="text-[#A1A1AA] text-sm text-center mb-6 max-w-md mx-auto">
          El repositorio <code className="text-white">{src.url}</code> se perdió tras un
          reinicio del servidor (los archivos se almacenan en disco efímero).
          {status.rehydrate_reason === 'needs_token'
            ? ' Como es privado, pega tu Personal Access Token para reimportarlo automáticamente.'
            : ' Haz clic en Reimportar para recuperarlo.'}
        </p>
        <div className="space-y-3 max-w-md mx-auto">
          <div className="flex items-center gap-2">
            <input
              id="repo-private-toggle-rehydrate"
              type="checkbox"
              checked={showTokenField}
              onChange={(e) => setShowTokenField(e.target.checked)}
              data-testid="repo-rehydrate-private-toggle"
              className="w-4 h-4 rounded border-white/20 bg-[#050505] accent-white"
            />
            <label htmlFor="repo-private-toggle-rehydrate" className="text-xs text-[#A1A1AA] cursor-pointer select-none">
              Repositorio privado (proveer Personal Access Token)
            </label>
          </div>
          {showTokenField && (
            <input
              type="password"
              value={githubToken}
              onChange={(e) => setGithubToken(e.target.value)}
              placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
              data-testid="repo-rehydrate-token"
              autoComplete="off"
              spellCheck={false}
              className="w-full bg-[#050505] border border-white/10 text-white rounded-md px-3 py-2 text-sm placeholder-[#52525B] focus:outline-none focus:border-white/30 font-mono"
            />
          )}
          <button
            type="button"
            disabled={importing}
            onClick={onRehydrate}
            data-testid="repo-rehydrate-button"
            className="w-full bg-white text-black font-medium rounded-md py-2.5 text-sm hover:bg-neutral-200 disabled:opacity-50 transition-colors flex items-center justify-center"
          >
            {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
            {importing ? 'Reimportando…' : 'Reimportar ahora'}
          </button>
        </div>
      </motion.div>
    );
  }

  if (!status?.has_import) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-[#0E0F11] border border-white/5 rounded-xl p-8"
        data-testid="repo-empty"
      >
        <GitBranch className="w-12 h-12 text-[#52525B] mx-auto mb-4" strokeWidth={1.5} />
        <h3 className="text-xl text-white text-center mb-2">Aún no hay código en este workspace</h3>
        <p className="text-[#A1A1AA] text-sm text-center mb-6">
          Importa un repositorio Git para que las misiones <code>analyze</code> y <code>repair</code> puedan leer archivos reales.
        </p>
        <div className="space-y-3 max-w-md mx-auto">
          <input
            type="text"
            value={gitUrl}
            onChange={(e) => setGitUrl(e.target.value)}
            placeholder="https://github.com/usuario/repo.git"
            data-testid="repo-git-url"
            className="w-full bg-[#050505] border border-white/10 text-white rounded-md px-3 py-2 text-sm placeholder-[#52525B] focus:outline-none focus:border-white/30"
          />
          <input
            type="text"
            value={branch}
            onChange={(e) => setBranch(e.target.value)}
            placeholder="main"
            data-testid="repo-git-branch"
            className="w-full bg-[#050505] border border-white/10 text-white rounded-md px-3 py-2 text-sm placeholder-[#52525B] focus:outline-none focus:border-white/30"
          />
          <div className="flex items-center gap-2">
            <input
              id="repo-private-toggle"
              type="checkbox"
              checked={showTokenField}
              onChange={(e) => setShowTokenField(e.target.checked)}
              data-testid="repo-private-toggle"
              className="w-4 h-4 rounded border-white/20 bg-[#050505] accent-white"
            />
            <label htmlFor="repo-private-toggle" className="text-xs text-[#A1A1AA] cursor-pointer select-none">
              Repositorio privado (usar Personal Access Token de GitHub)
            </label>
          </div>
          {showTokenField && tokenSaved && !replacingToken && (
            <div
              data-testid="repo-token-saved"
              className={`rounded-md border px-3 py-2 text-xs flex items-center justify-between gap-2 ${
                tokenInvalid
                  ? 'border-amber-500/30 bg-amber-950/20 text-amber-200'
                  : 'border-emerald-500/20 bg-emerald-950/20 text-emerald-200'
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="font-medium">
                  {tokenInvalid ? 'Token guardado · rechazado por GitHub' : 'Token guardado'}
                </div>
                <div className="text-[10px] text-[#A1A1AA] font-mono truncate">
                  {storedToken?.github_username ? `${storedToken.github_username} · ` : ''}
                  {storedToken?.masked_preview}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => { setReplacingToken(true); setGithubToken(''); }}
                  data-testid="repo-token-replace"
                  className="px-2 py-1 text-[10px] rounded bg-white/10 hover:bg-white/20 text-white"
                >Reemplazar</button>
                <button
                  type="button"
                  onClick={deleteToken}
                  data-testid="repo-token-delete"
                  className="px-2 py-1 text-[10px] rounded bg-red-900/40 hover:bg-red-900/60 text-red-200"
                >Eliminar</button>
              </div>
            </div>
          )}
          {tokenInputVisible && (
            <div className="space-y-1">
              <input
                type="password"
                value={githubToken}
                onChange={(e) => setGithubToken(e.target.value)}
                placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
                data-testid="repo-github-token"
                autoComplete="off"
                spellCheck={false}
                className="w-full bg-[#050505] border border-white/10 text-white rounded-md px-3 py-2 text-sm placeholder-[#52525B] focus:outline-none focus:border-white/30 font-mono"
              />
              <p className="text-[10px] text-[#52525B] leading-snug">
                Se guardará cifrado para que no tengas que pegarlo otra vez. Se invalidará automáticamente si GitHub lo rechaza.
                {replacingToken && (
                  <>
                    {' '}
                    <button
                      type="button"
                      onClick={() => { setReplacingToken(false); setGithubToken(''); }}
                      className="underline text-[#A1A1AA] hover:text-white"
                    >Cancelar reemplazo</button>
                  </>
                )}
              </p>
            </div>
          )}
          <button
            type="button"
            disabled={importing || !gitUrl}
            onClick={onImport}
            data-testid="repo-import-button"
            className="w-full bg-white text-black font-medium rounded-md py-2.5 text-sm hover:bg-neutral-200 disabled:opacity-50 transition-colors flex items-center justify-center"
          >
            {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
            {importing ? 'Importando…' : 'Importar repositorio'}
          </button>
        </div>
      </motion.div>
    );
  }

  const allNodes = tree ? flattenTree(tree, openDirs) : [];
  const filtered = filter
    ? allNodes.filter((n) => n.path.toLowerCase().includes(filter.toLowerCase()))
    : allNodes;

  // B20/B41 — Virtualize the tree once it grows beyond ~250 visible nodes.
  // Each row is ~24px tall.
  const virtualize = filtered.length > VIRTUALIZE_THRESHOLD;
  const overscan = 8;
  const firstIdx = virtualize ? Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - overscan) : 0;
  const lastIdx = virtualize
    ? Math.min(filtered.length, Math.ceil((scrollTop + viewportH) / ROW_HEIGHT) + overscan)
    : filtered.length;
  const visible = filtered.slice(firstIdx, lastIdx);
  const totalHeight = virtualize ? filtered.length * ROW_HEIGHT : ('auto' as const);
  const topPad = virtualize ? firstIdx * ROW_HEIGHT : 0;

  return (
    <div className="space-y-4">
      <div className="bg-[#0E0F11] border border-white/5 rounded-xl p-4 flex items-center justify-between flex-wrap gap-2" data-testid="repo-info">
        <div>
          <p className="text-xs font-mono uppercase text-[#52525B] mb-1">Importado</p>
          <p className="text-sm text-white font-mono break-all">{status.import_source?.url || status.imported_path}</p>
          <p className="text-xs text-[#52525B] mt-1">
            {status.statistics?.total_files} archivos · {status.statistics?.lines_of_code} LoC
          </p>
        </div>
        <div className="flex items-center gap-2">
          {indexStatus !== null && (
            <IndexingBadge
              status={indexStatus}
              reindexing={reindexing}
              onReindex={async () => {
                setReindexing(true);
                try {
                  await apiService.workspaceReindex(workspaceId, false);
                  for (let i = 0; i < 60; i++) {
                    await new Promise((r) => setTimeout(r, 2000));
                    try {
                      const ix = await apiService.workspaceIndexStatus(workspaceId);
                      setIndexStatus(ix);
                      if (ix.indexing_state === 'ready' || ix.indexing_state === 'error') {
                        break;
                      }
                    } catch {
                      /* keep polling */
                    }
                  }
                } finally {
                  setReindexing(false);
                }
              }}
            />
          )}
          <button
            type="button"
            onClick={refresh}
            data-testid="repo-refresh"
            className="text-xs text-[#A1A1AA] hover:text-white px-3 py-1.5 border border-white/10 rounded-md hover:bg-white/5 transition-colors"
          >
            Recargar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div
          ref={treeScrollRef}
          className="md:col-span-1 bg-[#0E0F11] border border-white/5 rounded-xl p-3 max-h-[60vh] overflow-y-auto"
          data-testid="repo-tree"
        >
          <div className="flex items-center mb-2 px-2">
            <Search className="w-3 h-3 text-[#52525B] mr-1.5" />
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filtrar…"
              data-testid="repo-tree-filter"
              className="flex-1 bg-transparent text-xs text-white placeholder-[#52525B] outline-none"
            />
            {virtualize && (
              <span className="ml-2 text-[10px] font-mono text-[#52525B]" data-testid="repo-tree-virtualized">
                {filtered.length.toLocaleString()} ítems
              </span>
            )}
          </div>
          <div style={{ height: totalHeight, position: 'relative' }}>
            <div style={{ transform: `translateY(${topPad}px)` }}>
              {visible.map((n) => (
                <button
                  key={`${n.type}-${n.path}`}
                  type="button"
                  data-testid={`repo-${n.type}-${n.path}`}
                  onClick={() => (n.type === 'dir' ? toggleDir(n.path) : onSelectFile(n.path))}
                  className={`w-full text-left flex items-center px-2 rounded text-xs ${
                    selectedFile === n.path && n.type === 'file'
                      ? 'bg-white/10 text-white'
                      : 'text-[#A1A1AA] hover:bg-white/5'
                  }`}
                  style={{ paddingLeft: `${n.depth * 12 + 6}px`, height: ROW_HEIGHT }}
                >
                  {n.type === 'dir' ? (
                    openDirs.has(n.path) ? (
                      <FolderOpen className="w-3.5 h-3.5 mr-1.5 text-[#A1A1AA]" />
                    ) : (
                      <Folder className="w-3.5 h-3.5 mr-1.5 text-[#A1A1AA]" />
                    )
                  ) : (
                    fileIconFor(n.name)
                  )}
                  <span className="ml-1.5 truncate">{n.name}</span>
                  {n.type === 'file' && (
                    <span className="ml-auto text-[#52525B] font-mono text-[10px]">{n.size}b</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="md:col-span-2 bg-[#0E0F11] border border-white/5 rounded-xl overflow-hidden" data-testid="repo-viewer">
          <div className="border-b border-white/5 px-4 py-2 flex items-center justify-between">
            <p className="text-xs font-mono text-[#A1A1AA] truncate">
              {selectedFile || 'Selecciona un archivo'}
            </p>
            {selectedFile && (
              <span className="text-[10px] font-mono text-[#52525B]">read-only</span>
            )}
          </div>
          <pre
            data-testid="repo-file-content"
            className="text-xs text-[#E5E5E5] font-mono p-4 overflow-auto max-h-[55vh] whitespace-pre-wrap"
          >
            {selectedFile ? fileContent : 'Haz click en cualquier archivo del árbol para verlo aquí.'}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default RepoPanel;
